import eventBus from './EventBus';

export type EventDetail = {
    [key: string]: string|number|EventDetail
}

export type EventCallback<T extends EventDetail = EventDetail> = (args: T) => void;

type EventOptions = {
    bubbles: boolean
}

export enum CustomEventNames {
    SidePanelClose = 'side-panel:close',
    SidePanelOpen = 'side-panel:open',
    FormSubmit = 'form:submit',
    FormIsDirty = 'subform:dirty',
    ModalOpen = 'modal:open',
    ModalClose = 'modal:close',
    ChangeLanguage = 'language:change',
    FiltersApplied = 'filters:applied',
    FiltersDisabled = 'filters:disabled',
    HeaderScrolled = 'header:scrolled',
    HeaderUnscrolled = 'header:unscrolled',
    ItemDeleted = 'item:deleted',
    ItemSaved = 'item:saved',
    LoadingShow = 'loading:show',
    LoadingHide = 'loading:hide',
    AlertShow = 'alert:show',
    AlertHide = 'alert:hide',
    AlertDismiss = 'alert:dismiss',
    WishlistModalOpen = 'wishlist-modal:open',
}

type EventNames = keyof typeof CustomEventNames;

const defaultOptions = {
    bubbles: true
};

/**
 * Dispatches an event via Stimulus EventBus, replacing DOM-based dispatchEvent.
 * Signature matches previous pattern, but uses application + typed enum.
 */
export function dispatchEvent<T extends EventNames>(
    eventName: T,
    detail?: EventDetail
): void {
    eventBus.post(eventName, detail);
}

/**
 * Use dispatchDomEvent to facilitate custom event communication in a standard fashion
 * between unrelated controllers
 */
export const dispatchDomEvent = <T extends EventNames>(
    element: HTMLElement,
    eventName: T,
    detail: EventDetail,
    options?: EventOptions = defaultOptions
): void => {
    const event = new CustomEvent(eventName, {
        ...options,
        detail,
    });
    element.dispatchEvent(event);
};

/**
 * Subscribes to a custom event via EventBus
 */
export function subscribeEvent<T extends EventDetail = EventDetail>(
    eventName: CustomEventNames,
    callback: EventCallback<T>
): void {
    eventBus.subscribe(eventName, callback as EventCallback);
}

/**
 * Unsubscribes from a custom event via EventBus
 */
export function unsubscribeEvent<T extends EventDetail = EventDetail>(
    eventName: CustomEventNames,
    callback: EventCallback<T>
): void {
    eventBus.unsubscribe(eventName, callback as EventCallback);
}
